import { Dictionary, isEqual, map, omit, pick } from 'lodash';
import { useMemo } from 'react';

import { CalculateFeaturesParams } from 'interfaces/job';
import PostProcessingAction, { PostProcessingActionCreated } from 'interfaces/postProcessingAction';
import { VisualizationCreated } from 'interfaces/visualization';
import { buildPreviousJobComparisonStatus, PreviousJobComparisonStatus } from './previousJobComparisonStatus';
import { convertFeatureActionsToBackend, convertPostProcessingActionsToBackend } from './utils';

interface NewCellLike {
  name: string;
  value: any;
  stain: string;
}

interface UsePreviousJobConfigurationStatusParams {
  hasLoadedPreviousJobSelections: boolean;
  jobParams?: CalculateFeaturesParams;
  postProcessingActionsById: Dictionary<PostProcessingAction>;
  postProcessingActions: PostProcessingActionCreated[];
  featureActions: PostProcessingActionCreated[];
  newCellsCreated: Record<string, NewCellLike>;
  visualizations: VisualizationCreated[];
}

interface PreviousJobConfigurationStatus {
  postProcessingStatus: PreviousJobComparisonStatus;
  featureStatus: PreviousJobComparisonStatus;
  visualizationStatus: PreviousJobComparisonStatus;
}

type ComparablePostProcessingAction = Omit<
  PostProcessingActionCreated,
  'id' | 'newOptionValues' | 'newOptionValueInput' | 'dependentOnInputTypes'
>;

const stripUiOnlyActionFields = (actions: PostProcessingActionCreated[] = []): ComparablePostProcessingAction[] =>
  map(actions, (action) => omit(action, ['id', 'newOptionValues', 'newOptionValueInput', 'dependentOnInputTypes']));

const getActionIdentitySnapshot = (actions: ComparablePostProcessingAction[] = []) =>
  map(actions, (action) => pick(action, ['actionId', 'actionName', 'actionInput', 'stain']));

const stripUiOnlyVisualizationFields = (visualizations: Array<Record<string, any>> = []) =>
  map(visualizations, (visualization) => omit(visualization, ['newVisualizationId']));

const getVisualizationIdentitySnapshot = (visualizations: Array<Record<string, any>> = []) =>
  map(stripUiOnlyVisualizationFields(visualizations), (visualization) =>
    omit(visualization, ['layers', 'withComposite'])
  );

export const usePreviousJobConfigurationStatus = ({
  hasLoadedPreviousJobSelections,
  jobParams,
  postProcessingActionsById,
  postProcessingActions,
  featureActions,
  newCellsCreated,
  visualizations,
}: UsePreviousJobConfigurationStatusParams): PreviousJobConfigurationStatus => {
  const currentPostProcessingActions = useMemo(
    () =>
      stripUiOnlyActionFields(convertPostProcessingActionsToBackend(postProcessingActionsById, postProcessingActions)),
    [postProcessingActionsById, postProcessingActions]
  );

  const previousPostProcessingActions = useMemo(
    () => stripUiOnlyActionFields((jobParams?.postprocessing as PostProcessingActionCreated[]) ?? []),
    [jobParams]
  );

  const currentFeatureActions = useMemo(
    () =>
      stripUiOnlyActionFields(
        convertFeatureActionsToBackend(postProcessingActionsById, featureActions, newCellsCreated as any)
      ),
    [postProcessingActionsById, featureActions, newCellsCreated]
  );

  const previousFeatureActions = useMemo(
    () => stripUiOnlyActionFields((jobParams?.features as PostProcessingActionCreated[]) ?? []),
    [jobParams]
  );

  const currentVisualizations = useMemo(
    () => stripUiOnlyVisualizationFields(visualizations as Array<Record<string, any>>),
    [visualizations]
  );

  const previousVisualizations = useMemo(
    () => stripUiOnlyVisualizationFields((jobParams?.visualizations as Array<Record<string, any>>) ?? []),
    [jobParams]
  );

  const postProcessingStatus = useMemo(() => {
    const actionSelectionsMatch = isEqual(
      getActionIdentitySnapshot(previousPostProcessingActions),
      getActionIdentitySnapshot(currentPostProcessingActions)
    );
    const actionSettingsMatch =
      actionSelectionsMatch && isEqual(previousPostProcessingActions, currentPostProcessingActions);

    return buildPreviousJobComparisonStatus({
      hasPreviousJobSettings: hasLoadedPreviousJobSelections,
      comparisons: [
        { label: 'selected actions', matches: actionSelectionsMatch },
        { label: 'action settings', matches: actionSettingsMatch },
      ],
    });
  }, [hasLoadedPreviousJobSelections, previousPostProcessingActions, currentPostProcessingActions]);

  const featureStatus = useMemo(() => {
    const featureSelectionsMatch = isEqual(
      getActionIdentitySnapshot(previousFeatureActions),
      getActionIdentitySnapshot(currentFeatureActions)
    );
    const featureSettingsMatch = featureSelectionsMatch && isEqual(previousFeatureActions, currentFeatureActions);

    return buildPreviousJobComparisonStatus({
      hasPreviousJobSettings: hasLoadedPreviousJobSelections,
      comparisons: [
        { label: 'selected features', matches: featureSelectionsMatch },
        { label: 'feature settings', matches: featureSettingsMatch },
      ],
    });
  }, [hasLoadedPreviousJobSelections, previousFeatureActions, currentFeatureActions]);

  const visualizationStatus = useMemo(() => {
    const visualizationSelectionsMatch = isEqual(
      getVisualizationIdentitySnapshot(previousVisualizations),
      getVisualizationIdentitySnapshot(currentVisualizations)
    );
    const visualizationSettingsMatch =
      visualizationSelectionsMatch && isEqual(previousVisualizations, currentVisualizations);

    return buildPreviousJobComparisonStatus({
      hasPreviousJobSettings: hasLoadedPreviousJobSelections,
      comparisons: [
        { label: 'selected visualizations', matches: visualizationSelectionsMatch },
        { label: 'visualization settings', matches: visualizationSettingsMatch },
      ],
    });
  }, [hasLoadedPreviousJobSelections, previousVisualizations, currentVisualizations]);

  return {
    postProcessingStatus,
    featureStatus,
    visualizationStatus,
  };
};
